import {
  PIPELINE_SOCIAL_PLATFORM_IDS,
  type PipelineSocialPlatform,
} from "@shared/pipeline-social-platforms";

/**
 * Client-side presentation for the pipeline social platforms (VP-299).
 *
 * The ids live in `@shared/pipeline-social-platforms` (shared with the API Zod
 * contract); brand labels and Tailwind colour classes live here because they are
 * pure presentation. Colours follow the existing scheduler palette
 * (`components/scheduler/SchedulerCalendar.tsx`) so the two views agree.
 */

export type PipelineSocialPlatformMeta = {
  id: PipelineSocialPlatform;
  /** Brand name — not translated (same in every locale). */
  label: string;
  /** Short badge label used on the dense board card. */
  shortLabel: string;
  /** Solid fill for the card stripe. */
  stripeClass: string;
  /** Tinted chip (border + background + text) for the drawer multi-select. */
  chipClass: string;
};

export const PIPELINE_SOCIAL_PLATFORMS: PipelineSocialPlatformMeta[] = [
  {
    id: "youtube",
    label: "YouTube",
    shortLabel: "YT",
    stripeClass: "bg-red-500",
    chipClass: "border-red-500/40 bg-red-500/10 text-red-200",
  },
  {
    id: "tiktok",
    label: "TikTok",
    shortLabel: "TT",
    stripeClass: "bg-pink-500",
    chipClass: "border-pink-500/40 bg-pink-500/10 text-pink-200",
  },
  {
    id: "instagram",
    label: "Instagram",
    shortLabel: "IG",
    stripeClass: "bg-fuchsia-500",
    chipClass: "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200",
  },
  {
    id: "facebook",
    label: "Facebook",
    shortLabel: "FB",
    stripeClass: "bg-blue-500",
    chipClass: "border-blue-500/40 bg-blue-500/10 text-blue-200",
  },
];

const PLATFORM_META_BY_ID = new Map<PipelineSocialPlatform, PipelineSocialPlatformMeta>(
  PIPELINE_SOCIAL_PLATFORMS.map((platform) => [platform.id, platform])
);

/**
 * Resolves ids to their presentation meta in the canonical order above, so the
 * stripe segments keep the same order regardless of click order. Unknown ids
 * (legacy/hand-edited snapshots) are dropped rather than rendered raw.
 */
export function resolvePipelineSocialPlatforms(
  ids: readonly string[] | undefined
): PipelineSocialPlatformMeta[] {
  if (!ids?.length) return [];
  const selected = new Set(ids);
  return PIPELINE_SOCIAL_PLATFORM_IDS.filter((id) => selected.has(id)).map(
    (id) => PLATFORM_META_BY_ID.get(id)!
  );
}
